import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import styles from './ToolTip.module.css';
import classNames from 'classnames';

const Tooltip = ({ children, content, mode = "dark", size = "sm", placement = "top" }) => {
  const [visible, setVisible] = useState(false);
  const [calculatedPlacement, setCalculatedPlacement] = useState(placement);
  const [styleOverrides, setStyleOverrides] = useState({});
  const tooltipRef = useRef(null);
  const scrollCloseRef = useRef(false); // Track whether popup was closed by scroll

  useEffect(() => {
    if (!visible) {
      setCalculatedPlacement(placement);
      setStyleOverrides({});
    }
  }, [visible, placement]);

  const updatePosition = useCallback(() => {
    if (!visible || !tooltipRef.current || typeof window === 'undefined') {
      return;
    }

    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let nextPlacement = calculatedPlacement;
    const rect = tooltipRef.current.getBoundingClientRect();

    if (nextPlacement === 'top' && rect.top < margin && rect.bottom < viewportHeight - margin) {
      nextPlacement = 'bottom';
    } else if (nextPlacement === 'bottom' && rect.bottom > viewportHeight - margin && rect.top > margin) {
      nextPlacement = 'top';
    }

    if (nextPlacement !== calculatedPlacement) {
      setCalculatedPlacement(nextPlacement);
      return;
    }

    let shiftX = 0;
    if (rect.left < margin) {
      shiftX = margin - rect.left;
    } else if (rect.right > viewportWidth - margin) {
      shiftX = (viewportWidth - margin) - rect.right;
    }

    let shiftY = 0;
    if (rect.top < margin) {
      shiftY = margin - rect.top;
    } else if (rect.bottom > viewportHeight - margin) {
      shiftY = (viewportHeight - margin) - rect.bottom;
    }

    setStyleOverrides({
      '--tooltip-shift-x': `${shiftX}px`,
      '--tooltip-shift-y': `${shiftY}px`,
    });
  }, [visible, calculatedPlacement]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, calculatedPlacement, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [visible, updatePosition]);

  // Close tooltip on mobile scroll/touchmove/wheel. Set scrollCloseRef so next open works.
  useEffect(() => {
    if (!visible) return;

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (!isMobile) return;

    let closed = false;
    const onMobileScroll = (e) => {
      if (!closed) {
        closed = true;
        setVisible(false);
        scrollCloseRef.current = true; // Set marker: closed by scroll
      }
    };
    window.addEventListener('scroll', onMobileScroll, { passive: true });
    window.addEventListener('touchmove', onMobileScroll, { passive: true });
    window.addEventListener('wheel', onMobileScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onMobileScroll, { passive: true });
      window.removeEventListener('touchmove', onMobileScroll, { passive: true });
      window.removeEventListener('wheel', onMobileScroll, { passive: true });
    };
  }, [visible]);

  // Helper to open. If last close was by scroll, allow reopen.
  const handleOpen = (e) => {
    if (!visible) {
      scrollCloseRef.current = false; // Always reset upon opening
      setVisible(true);
    }
  };
  const handleClose = (e) => {
    if (visible) {
      setVisible(false);
      scrollCloseRef.current = false; // Normal close, not from mobile scroll
    }
  };

  // Trick: on any focus/click/touch that is not already visible, always reset scrollCloseRef and show!
  // This ensures on mobile, after scroll-close, it can be opened again.

  return (
    <div
      className={styles.tooltipWrapper}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleClose}
      tabIndex={0}
      onClick={handleOpen}
      onTouchStart={handleOpen}
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          className={classNames(
            styles.tooltipBox,
            styles[mode],
            styles[size],
            styles[calculatedPlacement]
          )}
          role="tooltip"
          style={styleOverrides}
        >
          {content}
          <span className={styles.tooltipArrow} />
        </div>
      )}
    </div>
  );
};

export default Tooltip;